<template>
  <div ref="element" class="fixed inset-x-0 top-0 z-50 flex h-navbar-height flex-col pb-10">
    <nav class="mt-auto flex min-w-[30rem] items-end justify-between px-6 pt-4 navigation sm:px-12">
      <!-- LOGO -->
      <NuxtLink

        :to="localePath('/')"
        :aria-label="$t('aria_labels.home')"
        class="mb-1 block h-6 text-blue-500"
      >
        <IconLogo class="h-5 sm:h-6" />
      </NuxtLink>

      <div ref="navigationContainer" class="relative w-full pl-16">
        <div ref="dropdownContainer" class="opacity-0">
          <!-- Dropdown Background -->
          <div
            ref="dropdownBackground"
            class="pointer-events-none absolute top-full mt-11 size-0 rounded-bl-[3.75rem] bg-blue-500"
          />

          <!-- Dropdown Arrow -->
          <div
            ref="dropdownArrow"
            class="
              pointer-events-none absolute top-full mt-6 size-12 -translate-x-1/2 translate-y-8 rotate-45
              scale-0 bg-blue-500
            "
          />
        </div>

        <div class="flex items-center justify-between">
          <!-- META NAVIGATION -->
          <ul ref="metaNavigation" class="mb-4 flex gap-6 typo-body-lg">
            <li
              v-for="{ page } in globals?.main_meta?.tree"
              :key="page.id"
            >
              <NuxtLink :to="page.url">
                {{ page.title }}
              </NuxtLink>
            </li>
          </ul>
          <!-- ACTIONS -->
          <ul class="flex items-center gap-6">
            <!-- Search -->
            <li>
              <NuxtLink :to="localePath('search')" class="flex items-center gap-2 typo-body-lg">
                <IconMagnifyingGlass class="size-[1.25em]" />
                <span>{{ $t('search.search') }}</span>
              </NuxtLink>
            </li>

            <!-- Language Switcher -->
            <li>
              <LangSwitcher size="sm" />
            </li>

            <li ref="portalButton" class="flex h-container-lg">
              <NuxtLink
                v-if="globals?.general?.portal_link"
                :to="globals?.general?.portal_link"
                :target="useIsExternalUrl(globals?.general?.portal_link) ? '_blank' : '_self'"
                class="
                  flex h-full items-center justify-center rounded-full bg-blue-500 px-6 text-white
                  !no-underline transition-colors typo-button-text-sm
                  hover:bg-blue-600
                "
              >
                {{ globals?.general?.portal_link_text }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- NAVIGATION -->
        <ul class="mt-1 flex gap-8">
          <li
            v-for="item in globals?.main?.tree"
            :key="item.page.id"
          >
            <div>
              <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
              <Component
                :is="buttonOrNuxtLink(item.page.url)"
                :to="item.page.url && item.page.url"
                class="typo-heading-sm"
                :class="useIsRouterLinkActive(item.page.url, route.path)"
                :target="item.page.url ? useIsExternalUrl(item.page.url) ? '_blank' : '_self' : null"
                :aria-expanded="item.children?.length ? selectedNavItem === item : undefined"
                :aria-controls="item.children?.length ? `sub-nav-${item.page.id}` : undefined"
                @keydown.space.prevent="item.children?.length ? selectedNavItem ? leave() : enter(item) : null"
                @blur="item.children?.length ? leave() : null"
                @mouseenter="item.children?.length ? enter(item) : null"
                @mouseleave="item.children?.length ? leave() : null"
              >
                {{ item.page.title }}
              </Component>
            </div>

            <template v-if="item.children?.length">
              <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
              <SubNavigation
                v-show="selectedNavItem === item"
                :id="`sub-nav-${item.page.id}`"
                :key="item.page.id"
                class="router-link-exact mt-11"
                :children="item.children"
                :parent="item.page"
                @has-focus-within="$event ? enter(item) : leave()"
                @mouseenter="enter(item)"
                @mouseleave="leave()"
              />
            </template>
          </li>
        </ul>
      </div>
    </nav>
  </div>
</template>

<script lang="ts" setup>
import gsap from 'gsap'

const globals = inject(GlobalsKey, null)

const element = ref<HTMLDivElement | null>(null)
const navigationContainer = ref<HTMLDivElement | null>(null)
const metaNavigation = ref<HTMLDivElement | null>(null)
const portalButton = ref<HTMLAnchorElement | null>(null)

const dropdownContainer = ref<HTMLDivElement | null>(null)
const dropdownBackground = ref<HTMLDivElement | null>(null)
const dropdownArrow = ref<HTMLDivElement | null>(null)

const route = useRoute()
const localePath = useLocalePath()
const selectedNavItem = ref(null)

let enterTimeout: number | null = null
let leaveTimeout: number | null = null

let tlItems: gsap.core.Timeline | null = null
let tlBackground: gsap.core.Timeline | null = null

async function enter(item: any) {
  if (enterTimeout === null) {
    enterTimeout = setTimeout(async () => {
      // If the Subnav item has no children close the dropdown
      if (!item || !item.children?.length) {
        leave()
      }

      // Clear the leave timeout in case a leave is already in progress
      clearLeaveTimeout()

      // Check if the subnav is already open, if yes nothing to do
      if (selectedNavItem.value === item) {
        return
      }

      const subnavAlreadyOpen = !!selectedNavItem.value
      // Reverse and revert the timeline if sub navigation is already open but displaying a different item
      if (subnavAlreadyOpen && tlItems) {
        tlItems.timeScale(2)
        await tlItems.reverse()
        tlItems.timeScale(1)
        tlItems.revert()
      }

      // Set the selected item
      selectedNavItem.value = item

      // Wait until the DOM is updated
      await nextTick()

      // Get the current subnav element
      const subNav = document.getElementById(`sub-nav-${item.page.id}`)
      if (!subNav) return

      // Set the position of the subnav element specially the left position
      setSubNavPosition(subNav)

      // Get the computed style of the subnav to pass it to the background and arrow animation
      const computedStyle = window.getComputedStyle(subNav)

      // Kill existing timelines to prevent conflicts
      if (tlItems) tlItems.kill()
      if (tlBackground) tlBackground.kill()

      // Create the timeline for the background
      tlBackground = gsap.timeline({
        defaults: {
          duration: subnavAlreadyOpen ? 0.3 : 0.6,
          ease: 'power1.inOut',
        },
      })

      // Create the timeline for the subnav items
      tlItems = gsap.timeline({
        defaults: {
          duration: 0.25,
          ease: 'power1.inOut',
        },
      })

      // Ensure the timeScale is 1
      tlItems.timeScale(1)

      // Get the elements to animate
      const slideUpItems = subNav?.querySelectorAll('.slide-up')
      const slideRightItems = subNav?.querySelectorAll('.slide-right')
      const slideScaleXItems = subNav?.querySelectorAll('.slide-scale-x')

      // Initially set the position of the arrow and background
      if (!subnavAlreadyOpen) {
        gsap.set(dropdownArrow.value, {
          left: getCurrentCenterLeft(subNav.parentElement),
        })
        gsap.set(dropdownBackground.value, {
          left: getCurrentCenterLeft(subNav.parentElement),
        })
      }

      // TW rotate-45 does not work as initial value so we set it here by gsap
      gsap.set(dropdownArrow.value, {
        rotate: 45,
      })

      // Animate the background and arrow
      tlBackground
        .to(dropdownContainer.value, {
          opacity: 1,
          ease: subnavAlreadyOpen ? 'power1.inOut' : 'back.out',
        })
        .to(dropdownBackground.value, {
          left: parseFloat(computedStyle.left),
          width: parseFloat(computedStyle.width),
          height: parseFloat(computedStyle.height),
          ease: subnavAlreadyOpen ? 'power1.inOut' : 'back.out',
        }, '<')
        .to(
          dropdownArrow.value,
          {
            left: getCurrentCenterLeft(subNav.parentElement),
            rotate: 45,
            scale: 1,
            translateY: 0,
          },
          subnavAlreadyOpen ? '<' : '<',
        )

      // Animate the subnav items
      await tlItems
        .to(slideUpItems, {
          autoAlpha: 1,
          translateY: 0,
          stagger: 0.05,
        }, '+=0.3')
        .to(slideRightItems, {
          autoAlpha: 1,
          translateX: 0,
          stagger: 0.05,
        }, '-=0.2')
        .to(slideScaleXItems, {
          autoAlpha: 1,
          scaleX: 1,
          stagger: 0.05,
        }, '-=0.1')
    }, 150) as unknown as number
  }
}

async function leave() {
  if (leaveTimeout === null) {
    leaveTimeout = setTimeout(() => {
      // If the timeline is not set, close the subnav
      if (!tlItems) {
        selectedNavItem.value = null
        return
      }

      const subNav = document.getElementById(`sub-nav-${selectedNavItem.value?.page.id}`)
      if (!subNav) return

      Promise.all([
        tlItems.to(subNav, {
          duration: 0.1,
          display: 'none',
          opacity: 0,
        }),
        tlBackground
          ?.to(dropdownContainer.value, {
            duration: 0.3,
            opacity: 0,
          })
          .to(dropdownArrow.value, {
            duration: 0.2,
            rotate: 45,
            scale: 0,
            translateX: '-50%',
            translateY: '2rem',
            ease: 'power1.inOut',
          }, '<')
          .set(dropdownBackground.value, {
            width: 0,
            height: 0,
          }),
      ]).then(() => {
        selectedNavItem.value = null
        tlItems?.revert()
        tlItems?.kill()
      })
    }, 750) as unknown as number
  }

  if (enterTimeout !== null) {
    clearTimeout(enterTimeout)
    enterTimeout = null
  }
}

function clearLeaveTimeout() {
  if (leaveTimeout !== null) {
    clearTimeout(leaveTimeout)
    leaveTimeout = null
  }
}

// Set the position of the subnav element specially the left position
function setSubNavPosition(subNav: HTMLElement | null) {
  if (!subNav) return

  const child = subNav
  const parent = child.parentElement

  const parentRect = parent.getBoundingClientRect()
  const childRect = child.getBoundingClientRect()
  const overflowRight = childRect.right - parentRect.right

  if (overflowRight > 0) {
    const currentLeft = parseFloat(getComputedStyle(child).left) || 0
    const newLeft = Math.max(0, currentLeft - overflowRight)
    child.style.left = `${newLeft}px`
  }
}

// Get the center left position of a element relative to the defined parent
function getCurrentCenterLeft(element: HTMLElement | null): number {
  if (!element || !navigationContainer.value) return 0

  const rect = element.getBoundingClientRect()

  const parentRect = navigationContainer.value.getBoundingClientRect()
  const centerLeft = rect.left - parentRect.left + (rect.width / 2)

  return centerLeft
}

function buttonOrNuxtLink(url: string) {
  if (url) {
    return resolveComponent('NuxtLink')
  }
  return 'button'
}

// Run hide() after every route change
const router = useRouter()
router.beforeEach(() => {
  leave()
})

let tlNavigationScroll: gsap.core.Timeline | null = null
onMounted(() => {
  if (element.value && metaNavigation.value) {
    gsap.set(element.value, {
      backgroundColor: 'transparent',
    })

    tlNavigationScroll = gsap.timeline({
      scrollTrigger: {
        trigger: document.body,
        start: 'top top',
        end: '500 top',
        scrub: 0.5,
      },
    })
      .to(element.value, {
        backgroundColor: '#FFFFFF',
        height: '6rem',
        boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.05)', // Target shadow
      }, 0)
      .to(metaNavigation.value, {
        marginBottom: '0.5rem',
      }, 0)
      .to(portalButton.value, {
        height: '2rem',
      }, 0)
  }
})

onUnmounted(() => {
  tlItems?.kill()
  tlBackground?.kill()
  tlNavigationScroll?.kill()
})
</script>

<style>
.navigation {
  .slide-up {
    @apply invisible translate-y-5;
  }

  .slide-right {
    @apply invisible translate-x-8;
  }
  .slide-scale-x {
    @apply invisible;
    transform: scaleX(0);
  }
  .router-link-active {
    text-decoration-line: underline;
    text-underline-offset: 0.4rem;
    text-decoration-thickness: 2px;
  }

  .router-link-exact-active {
    text-decoration-line: underline;
    text-underline-offset: 0.4rem;
    text-decoration-thickness: 2px;
  }
}
</style>
